/**
 * EquipmentEditPanel — numeric edit panel rendered below a wall SVG when
 * exactly one equipment is selected. Mirrors the 3D-scene side-panel
 * pattern: offset along wall · mount-elevation · rotation_deg (yaw, 0/90/180/270°)
 * · orientation_flip (swap thickness↔height — fixes panels entered with
 * depth=thickness) · Quick-orient preset menu.
 *
 * The component is intentionally presentational: it receives `item`,
 * the active `wall`, geometry hints, and an `onUpdate(id, patch)` callback
 * that the parent orchestrator wires into its mutation pipeline. No
 * project state lives here.
 */
import { useState } from "react";
import { ArrowClockwise, ArrowsVertical, ArrowsHorizontal, Lightning, CaretDown } from "@phosphor-icons/react";
import { flipVerticalAxisMap, flipHorizontalAxisMap, orientedDimensions } from "@/lib/orientation";

/** Quick-orient presets — single-click sets rotation_deg + orientation_flip
 *  + mount_height_in in one shot. `mountElev: null` = preserve current
 *  mount height (so user's custom values aren't overwritten).
 *
 *  Orientation math (see /lib/orientation.js):
 *    For an item entered as length_in=H, width_in=W, depth_in=D, the resulting
 *    box dimensions {wallSize, vertSize, thickness} are:
 *      rot=0, flip=false: {H, D, W}  → panel lying FLAT, face up
 *      rot=0, flip=true:  {H, W, D}  → mounted LANDSCAPE (long side horizontal)
 *      rot=90, flip=false:{D, H, W}  → narrow side on wall, panel vertical
 *      rot=90, flip=true: {W, H, D}  → mounted UPRIGHT (the usual wall panel)
 *  Pick presets so each labels what the user actually sees.
 */
const QUICK_PRESETS = [
  // The classic "tall panel against the wall" → 20 wide × 58 tall × 8 thick
  { id: "wall-tall",      label: "Wall mount · upright (tall)", icon: "▯", patch: { rotation_deg: 90, orientation_flip: true  }, mountElev: null },
  // Long side horizontal across the wall → 58 wide × 20 tall × 8 thick
  { id: "wall-landscape", label: "Wall mount · landscape",      icon: "▭", patch: { rotation_deg: 0,  orientation_flip: true  }, mountElev: null },
  // Lying flat on the floor with face up — what the user asked for v1.63:
  // "58 parallel to wall, 20 parallel to floor". With rot=0/flip=false:
  // wallSize=58 (length along wall), thickness=20 (depth perpendicular,
  // also parallel to floor), vertSize=8 (depth_in sticking up).
  { id: "floor-flat",     label: "Floor · lay flat (face up)",  icon: "▣", patch: { rotation_deg: 0,  orientation_flip: false }, mountElev: 0    },
  // Ceiling-suspended box, face down. Mount = ceiling - depth_in so it hugs.
  { id: "ceiling-flat",   label: "Ceiling drop · face down",    icon: "▤", patch: { rotation_deg: 0,  orientation_flip: false }, mountElev: 88   },
  // Legacy "back against wall" — same as wall-tall, kept for older sample data.
  { id: "wall-back",      label: "Wall mount · back→wall",      icon: "⊟", patch: { rotation_deg: 90, orientation_flip: true  }, mountElev: null },
];

/** Compute the box's width-on-wall + height-on-wall given an orientation
 *  patch. Used by the offset clamper so rotation/flip changes don't push
 *  the equipment past the wall edge ("PNL-002 sticking out of enclosure"
 *  bug from v1.59). Mirrors the math in WallPanel.jsx L436-441 + scene.js. */
/** v1.66 — _boxOnWall now delegates to the single source of truth in
 *  /lib/orientation.js so the 2D Size-tab editor stays in lockstep with
 *  the 3D scene + WallPanel render after axis_map flips. */
function _boxOnWall(item, wall, orientPatch = {}) {
  const merged = { ...item, ...orientPatch };
  const { wallSize, vertSize, thickness } = orientedDimensions(merged);
  return {
    widthOnWall: wall.axis === "x" ? wallSize : thickness,
    heightOnWall: vertSize,
  };
}

export default function EquipmentEditPanel({ item, wall, wallLen, H, onUpdate }) {
  const [presetOpen, setPresetOpen] = useState(false);
  const p = item.position || {};
  // v1.66 — derive the on-wall centerline from orientedDimensions, not the
  // stale pos.w_in/pos.h_in. Fixes the recurring "2D doesn't match 3D" report.
  const _od = orientedDimensions(item);
  const onWallW = wall.axis === "x" ? _od.wallSize : _od.thickness;
  const baseOffset = wall.axis === "x"
    ? p.x_in + onWallW / 2
    : p.y_in + onWallW / 2;
  const baseY = Math.max(0, Number(item.mount_height_in) || 0);
  const rotDeg = ((Number(item.rotation_deg) || 0) % 360 + 360) % 360;

  const setOffset = (newOff) => {
    if (!Number.isFinite(newOff)) return;
    const clamped = Math.max(0, Math.min(wallLen, newOff));
    onUpdate(item.id, { wallId: wall.id, offsetAlongWall: clamped, mountElevation: baseY });
  };
  const setElev = (newElev) => {
    if (!Number.isFinite(newElev)) return;
    const clamped = Math.max(0, Math.min(H, newElev));
    onUpdate(item.id, { wallId: wall.id, offsetAlongWall: baseOffset, mountElevation: clamped });
  };
  const setRotation = (deg) => {
    // Compute the post-rotation box and clamp the wall offset so the box
    // stays inside its enclosure (fixes the v1.59 "PNL-002 sticks out
    // after orientation change" bug).
    const { widthOnWall, heightOnWall } = _boxOnWall(item, wall, { rotation_deg: deg });
    const minOff = widthOnWall / 2;
    const maxOff = Math.max(minOff, wallLen - widthOnWall / 2);
    const clampedOff = Math.max(minOff, Math.min(maxOff, baseOffset));
    const maxElev = Math.max(0, H - heightOnWall);
    const clampedElev = Math.max(0, Math.min(maxElev, baseY));
    onUpdate(item.id, {
      wallId: wall.id, offsetAlongWall: clampedOff, mountElevation: clampedElev,
      patch: { rotation_deg: deg },
    });
  };
  const rotate90 = () => setRotation((rotDeg + 90) % 360);
  /** v1.64 — Flip Vertical/Horizontal (incremental 90° rotation from CURRENT
   *  pose). Computes the new axis_map, then re-clamps the position so the
   *  rotated bbox stays inside the room. Each call also clears legacy
   *  rotation_deg + orientation_flip so the two systems can't drift. */
  const flipVertical = () => {
    const newAm = flipVerticalAxisMap(item);
    const merged = { ...item, axis_map: newAm, rotation_deg: 0, orientation_flip: false };
    const { wallSize, vertSize, thickness } = orientedDimensions(merged);
    const widthOnWall = wall.axis === "x" ? wallSize : thickness;
    const minOff = widthOnWall / 2;
    const maxOff = Math.max(minOff, wallLen - widthOnWall / 2);
    const clampedOff = Math.max(minOff, Math.min(maxOff, baseOffset));
    const maxElev = Math.max(0, H - vertSize);
    const clampedElev = Math.max(0, Math.min(maxElev, baseY));
    onUpdate(item.id, {
      wallId: wall.id, offsetAlongWall: clampedOff, mountElevation: clampedElev,
      patch: { axis_map: newAm, rotation_deg: 0, orientation_flip: false },
    });
  };
  const flipHorizontal = () => {
    const newAm = flipHorizontalAxisMap(item);
    const merged = { ...item, axis_map: newAm, rotation_deg: 0, orientation_flip: false };
    const { wallSize, vertSize, thickness } = orientedDimensions(merged);
    const widthOnWall = wall.axis === "x" ? wallSize : thickness;
    const minOff = widthOnWall / 2;
    const maxOff = Math.max(minOff, wallLen - widthOnWall / 2);
    const clampedOff = Math.max(minOff, Math.min(maxOff, baseOffset));
    const maxElev = Math.max(0, H - vertSize);
    const clampedElev = Math.max(0, Math.min(maxElev, baseY));
    onUpdate(item.id, {
      wallId: wall.id, offsetAlongWall: clampedOff, mountElevation: clampedElev,
      patch: { axis_map: newAm, rotation_deg: 0, orientation_flip: false },
    });
  };
  const applyPreset = (preset) => {
    // mountElev = null means "preserve current" — don't clobber the
    // user's custom mount height with the preset's default.
    const presetMount = (preset.mountElev == null) ? baseY : preset.mountElev;
    const { widthOnWall, heightOnWall } = _boxOnWall(item, wall, preset.patch);
    const minOff = widthOnWall / 2;
    const maxOff = Math.max(minOff, wallLen - widthOnWall / 2);
    const clampedOff = Math.max(minOff, Math.min(maxOff, baseOffset));
    const maxElev = Math.max(0, H - heightOnWall);
    const clampedElev = Math.max(0, Math.min(maxElev, presetMount));
    onUpdate(item.id, {
      wallId: wall.id, offsetAlongWall: clampedOff,
      mountElevation: clampedElev,
      patch: preset.patch,
    });
    setPresetOpen(false);
  };

  return (
    <div className="mt-2 p-2 border border-[#FFCC00]/40 bg-[#0a0d12] font-mono text-[10px] flex items-end gap-2 flex-wrap"
         data-testid={`elev-edit-${item.tag}`}>
      <div className="label-tag !text-[9px] mr-1">// {item.tag}</div>
      <label className="flex flex-col gap-0.5">
        <span className="text-gray-500">Offset along {wall.label.split("·")[0].trim()} (in)</span>
        <input type="number" defaultValue={Math.round(baseOffset)}
               step="1" min="0" max={Math.round(wallLen)}
               onBlur={(e) => setOffset(Number(e.target.value))}
               onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
               className="w-20 bg-black/40 border border-white/10 px-1 py-0.5 focus:border-[#FFCC00] outline-none"
               data-testid={`elev-edit-${item.tag}-offset`} />
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-gray-500">Mount elev. (in)</span>
        <input type="number" defaultValue={Math.round(baseY)}
               step="1" min="0" max={Math.round(H)}
               onBlur={(e) => setElev(Number(e.target.value))}
               onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
               className="w-20 bg-black/40 border border-white/10 px-1 py-0.5 focus:border-[#FFCC00] outline-none"
               data-testid={`elev-edit-${item.tag}-elev`} />
      </label>
      <div className="flex flex-col gap-0.5">
        <span className="text-gray-500">Rotation</span>
        <div className="inline-flex border border-white/10">
          {[0, 90, 180, 270].map((deg) => (
            <button key={deg} type="button"
                    onClick={() => setRotation(deg)}
                    className={`px-1.5 py-0.5 ${
                      rotDeg === deg
                        ? "bg-[#00E5FF] text-black font-bold"
                        : "text-gray-400 hover:text-white"
                    }`}
                    data-testid={`elev-edit-${item.tag}-rot-${deg}`}>
              {deg}°
            </button>
          ))}
          <button type="button" onClick={rotate90}
                  className="px-1 py-0.5 border-l border-white/10 text-[#00E5FF] hover:bg-white/5"
                  title="Rotate 90° clockwise"
                  data-testid={`elev-edit-${item.tag}-rot-step`}>
            <ArrowClockwise size={10} weight="bold" />
          </button>
        </div>
      </div>
      {/* v1.64 — Flip Vertical & Flip Horizontal replace the single Flip
          button. Each rotates 90° from the current pose (not the original),
          composing through the axis_map permutation so all 6 cube
          orientations are reachable. Position auto-clamps to the room. */}
      <button type="button" onClick={flipVertical}
              className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 text-gray-300 hover:border-[#FFCC00] hover:text-[#FFCC00]"
              title="Flip Vertical — rotate 90° about the horizontal (X) axis from the current pose. Swaps vertical ↔ thickness dimensions; position auto-clamps so the box stays inside the room."
              data-testid={`elev-edit-${item.tag}-flip-vertical`}>
        <ArrowsVertical size={10} weight="bold" />
        Flip ↕ Vertical
      </button>
      <button type="button" onClick={flipHorizontal}
              className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 text-gray-300 hover:border-[#FFCC00] hover:text-[#FFCC00]"
              title="Flip Horizontal — rotate 90° about the vertical (Y) axis from the current pose. Swaps wall-parallel ↔ thickness dimensions; position auto-clamps so the box stays inside the room."
              data-testid={`elev-edit-${item.tag}-flip-horizontal`}>
        <ArrowsHorizontal size={10} weight="bold" />
        Flip ↔ Horizontal
      </button>
      {/* v1.67 — Reset Orientation in the 2D editor too (was only in 3D
          pre-v1.67). One click restores default axis_map. */}
      <button type="button"
              onClick={() => onUpdate(item.id, {
                wallId: wall.id, offsetAlongWall: baseOffset, mountElevation: baseY,
                patch: { axis_map: ["length", "depth", "width"], rotation_deg: 0, orientation_flip: false },
              })}
              className="inline-flex items-center gap-1 px-2 py-1 border border-white/15 text-gray-400 hover:border-[#00E5FF] hover:text-[#00E5FF]"
              title="Reset orientation to the default pose (length→X, depth→Y, width→Z)."
              data-testid={`elev-edit-${item.tag}-reset`}>
        ⟲ Reset
      </button>
      <div className="relative">
        <button type="button"
                onClick={() => setPresetOpen((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
                title="Quick orient presets — apply common rotation + flip + elevation combos in one click"
                data-testid={`elev-edit-${item.tag}-preset-toggle`}>
          <Lightning size={10} weight="fill" />
          Quick orient
          <CaretDown size={9} weight={presetOpen ? "fill" : "regular"} />
        </button>
        {presetOpen && (
          <div className="absolute z-30 top-full mt-1 left-0 bg-[#050608] border border-[#00E5FF]/40 shadow-xl w-[220px] py-1"
               data-testid={`elev-edit-${item.tag}-preset-menu`}>
            {QUICK_PRESETS.map((preset) => (
              <button key={preset.id} type="button"
                      onClick={() => applyPreset(preset)}
                      className="w-full px-2 py-1 text-left hover:bg-[#00E5FF]/10 flex items-center gap-2"
                      data-testid={`elev-edit-${item.tag}-preset-${preset.id}`}>
                <span className="text-[#00E5FF] w-3">{preset.icon}</span>
                <span className="flex-1 text-gray-200">{preset.label}</span>
                <span className="text-[8px] text-gray-500">@{preset.mountElev}″</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
